/**
 * EditMagasinModal — Carlos clarif §2 (2026-05-19).
 *
 * Symmetric 2-section modal for editing a magasin's printer config:
 *   - 📦 Imprimante Étiquettes (ZPL — Bixolon 50x30mm)
 *   - 🧾 Imprimante Tickets caisse + réparation (ESC/POS 80mm — Epson)
 *
 * Both sections share the same layout (IP, port, width info, status badge,
 * Test button). The Test button enqueues a server-side test job whose
 * `target_host` is baked from the magasin's saved IP — then polls the job
 * status (~10s) to report progress.
 */
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Tag, Receipt, X, Save, Loader2, CheckCircle2, AlertCircle,
  Send, Wifi, WifiOff,
} from 'lucide-react';
import MagasinHoursEditor from './MagasinHoursEditor';

const POLL_DELAY_MS = 1200;
const POLL_MAX_ATTEMPTS = 10;   // ~12s max

const PrinterSection = ({
  icon: Icon,
  title,
  subtitle,
  widthHint,
  ipValue,
  portValue,
  onIpChange,
  onPortChange,
  ipTestId,
  portTestId,
  testTestId,
  onTest,
  testing,
  testResult, // { state: 'idle'|'pending'|'ok'|'fail', message: string }
  configured,
}) => (
  <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4" data-testid={`printer-section-${ipTestId}`}>
    <div className="flex items-center gap-2 mb-3">
      <Icon className="w-5 h-5 text-cyan-300" />
      <div className="flex-1">
        <div className="text-sm font-semibold text-white">{title}</div>
        <div className="text-[11px] text-gray-400">{subtitle}</div>
      </div>
      {configured ? (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] border border-emerald-500/40 bg-emerald-500/15 text-emerald-300">
          <Wifi className="w-3 h-3" /> Configurée
        </span>
      ) : (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] border border-gray-500/30 bg-gray-500/15 text-gray-400">
          <WifiOff className="w-3 h-3" /> Non configurée
        </span>
      )}
    </div>

    <div className="grid grid-cols-[1fr_84px] gap-2 mb-3">
      <div>
        <label className="text-[10px] uppercase text-gray-500">IP</label>
        <input
          value={ipValue}
          onChange={(e) => onIpChange(e.target.value)}
          placeholder="192.168.1.50"
          className="w-full mt-1 px-2 py-1.5 rounded bg-black/30 border border-white/10 text-white text-sm font-mono focus:outline-none focus:border-cyan-500"
          data-testid={ipTestId}
        />
      </div>
      <div>
        <label className="text-[10px] uppercase text-gray-500">Port</label>
        <input
          type="number"
          value={portValue}
          onChange={(e) => onPortChange(parseInt(e.target.value, 10) || 9100)}
          placeholder="9100"
          className="w-full mt-1 px-2 py-1.5 rounded bg-black/30 border border-white/10 text-white text-sm font-mono focus:outline-none focus:border-cyan-500"
          data-testid={portTestId}
        />
      </div>
    </div>

    <div className="flex items-center justify-between gap-2">
      <span className="text-[10px] text-gray-500">{widthHint}</span>
      <button
        type="button"
        onClick={onTest}
        disabled={!configured || testing}
        className="px-3 py-1.5 rounded-lg bg-cyan-500/20 border border-cyan-500/40 text-cyan-200 hover:bg-cyan-500/30 disabled:opacity-40 disabled:cursor-not-allowed text-xs font-medium flex items-center gap-1.5"
        data-testid={testTestId}
      >
        {testing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
        Test impression
      </button>
    </div>

    {testResult && testResult.state !== 'idle' && (
      <div
        className={`mt-2 text-[11px] rounded-md px-2 py-1.5 flex items-center gap-1.5 ${
          testResult.state === 'ok'
            ? 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-200'
            : testResult.state === 'fail'
              ? 'bg-red-500/10 border border-red-500/30 text-red-200'
              : 'bg-amber-500/10 border border-amber-500/30 text-amber-200'
        }`}
        data-testid={`${testTestId}-result`}
      >
        {testResult.state === 'ok' && <CheckCircle2 className="w-3.5 h-3.5" />}
        {testResult.state === 'fail' && <AlertCircle className="w-3.5 h-3.5" />}
        {testResult.state === 'pending' && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
        <span>{testResult.message}</span>
      </div>
    )}
  </div>
);

const EditMagasinModal = ({ open, magasin, api, onClose, onSaved }) => {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [labelTest, setLabelTest] = useState({ state: 'idle', message: '' });
  const [ticketTest, setTicketTest] = useState({ state: 'idle', message: '' });
  const [labelTesting, setLabelTesting] = useState(false);
  const [ticketTesting, setTicketTesting] = useState(false);

  useEffect(() => {
    if (!open || !magasin) return;
    setForm({
      name: magasin.name || '',
      address: magasin.address || '',
      phone: magasin.phone || '',
      color: magasin.color || '#10b981',
      printer_ip: magasin.printer_ip || '',
      ticket_printer_ip: magasin.ticket_printer_ip || '',
      ticket_printer_port: magasin.ticket_printer_port || 9100,
      horaires: magasin.horaires || {},
      horaires_exceptions: magasin.horaires_exceptions || {},
      client_portal_enabled: !!magasin.client_portal_enabled,
    });
    setLabelTest({ state: 'idle', message: '' });
    setTicketTest({ state: 'idle', message: '' });
    setError('');
  }, [open, magasin]);

  if (!open || !magasin || !form) return null;
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setBusy(true);
    setError('');
    try {
      const cleanExc = {};
      Object.entries(form.horaires_exceptions || {}).forEach(([d, v]) => { if (d && /^\d{4}-\d{2}-\d{2}$/.test(d)) cleanExc[d] = v; });
      await api.patch(`/magasins/${magasin.code}`, { ...form, horaires_exceptions: cleanExc });
      onSaved?.();
      onClose?.();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Erreur de sauvegarde');
    } finally {
      setBusy(false);
    }
  };

  const pollJob = async (jobId) => {
    for (let i = 0; i < POLL_MAX_ATTEMPTS; i += 1) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, POLL_DELAY_MS));
      try {
        // eslint-disable-next-line no-await-in-loop
        const res = await api.get(`/printer/jobs/${jobId}/status`);
        const status = res?.data?.status;
        if (status === 'done' || status === 'completed') {
          return { ok: true, status };
        }
        if (status === 'failed' || status === 'error') {
          return { ok: false, status, error: res?.data?.error_message || 'Échec côté agent' };
        }
      } catch (_) { /* keep polling */ }
    }
    return { ok: false, status: 'timeout', error: "Agent n'a pas répondu (timeout). Vérifiez qu'il est connecté." };
  };

  const runTest = async (kind) => {
    const setTesting = kind === 'label' ? setLabelTesting : setTicketTesting;
    const setResult = kind === 'label' ? setLabelTest : setTicketTest;
    setTesting(true);
    setResult({ state: 'pending', message: 'Job envoyé. En attente de l\'agent…' });
    try {
      // First save the current IPs so the test uses fresh values.
      await api.patch(`/magasins/${magasin.code}`, {
        printer_ip: form.printer_ip,
        ticket_printer_ip: form.ticket_printer_ip,
        ticket_printer_port: form.ticket_printer_port,
      });
      const res = await api.post('/printer/test-magasin', { magasin_code: magasin.code, kind });
      const jobId = res?.data?.job_id;
      if (!jobId) throw new Error('Réponse invalide du serveur');
      const polled = await pollJob(jobId);
      if (polled.ok) {
        setResult({ state: 'ok', message: `Imprimé avec succès sur ${res?.data?.target_host}:${res?.data?.target_port}` });
      } else {
        setResult({ state: 'fail', message: polled.error });
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Erreur lors de l\'envoi du test';
      setResult({ state: 'fail', message: detail });
    } finally {
      setTesting(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      data-testid={`edit-magasin-modal-${magasin.code}`}
      onClick={onClose}
    >
      <div
        className="bg-gray-950 border border-white/10 rounded-2xl w-full max-w-2xl shadow-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <div className="flex items-center gap-2">
            <span
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: form.color || '#10b981' }}
            />
            <h2 className="text-base font-semibold text-white">
              Magasin <span className="font-mono text-cyan-300">{magasin.code}</span>
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded hover:bg-white/10 text-gray-400 hover:text-white"
            data-testid="edit-magasin-modal-close"
          >
            <X className="w-4 h-4" />
          </button>
        </header>

        <div className="p-5 space-y-4">
          {/* Informations de base */}
          <section className="space-y-3">
            <h3 className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">
              Informations
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="text-[10px] uppercase text-gray-500">Nom</label>
                <input
                  value={form.name}
                  onChange={(e) => upd('name', e.target.value)}
                  className="w-full mt-1 px-3 py-2 rounded-lg bg-black/30 border border-white/10 text-white text-sm"
                  data-testid={`magasin-edit-name-${magasin.code}`}
                />
              </div>
              <div className="col-span-2">
                <label className="text-[10px] uppercase text-gray-500">Adresse</label>
                <input
                  value={form.address}
                  onChange={(e) => upd('address', e.target.value)}
                  className="w-full mt-1 px-3 py-2 rounded-lg bg-black/30 border border-white/10 text-white text-sm"
                  data-testid={`magasin-edit-address-${magasin.code}`}
                />
              </div>
              <div>
                <label className="text-[10px] uppercase text-gray-500">Téléphone</label>
                <input
                  value={form.phone}
                  onChange={(e) => upd('phone', e.target.value)}
                  className="w-full mt-1 px-3 py-2 rounded-lg bg-black/30 border border-white/10 text-white text-sm"
                  data-testid={`magasin-edit-phone-${magasin.code}`}
                />
              </div>
              <div>
                <label className="text-[10px] uppercase text-gray-500">Couleur badge</label>
                <div className="flex items-center gap-2 mt-1">
                  <input
                    type="color"
                    value={form.color}
                    onChange={(e) => upd('color', e.target.value)}
                    className="w-10 h-8 rounded border border-white/10 bg-transparent cursor-pointer"
                  />
                  <input
                    value={form.color}
                    onChange={(e) => upd('color', e.target.value)}
                    className="flex-1 px-2 py-1.5 rounded bg-black/30 border border-white/10 text-white text-xs font-mono"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Horaires par magasin (rappel de fermeture de caisse) */}
          <MagasinHoursEditor
            horaires={form.horaires}
            exceptions={form.horaires_exceptions}
            code={magasin.code}
            onChange={({ horaires, exceptions }) => setForm((f) => ({ ...f, horaires, horaires_exceptions: exceptions }))}
          />

          {/* Espace client (portail) — réservé à la direction */}
          <section className="space-y-2">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                className="mt-1"
                checked={!!form.client_portal_enabled}
                onChange={(e) => upd('client_portal_enabled', e.target.checked)}
              />
              <span>
                <span className="block text-sm font-medium text-gray-800">Espace client activé pour ce magasin</span>
                <span className="block text-[11px] text-gray-500">
                  Autorise le paiement des services en ligne (ex. caricature) rattachés à ce magasin. Le suivi de
                  réparation d'un client vérifié reste accessible même si cette option est désactivée.
                </span>
              </span>
            </label>
          </section>

          {/* Imprimantes — 2 sections symétriques */}
          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">
                Imprimantes du magasin
              </h3>
              <span className="text-[10px] text-gray-600">
                Routées par l'agent v1.1+ via <span className="font-mono">target_host</span>
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <PrinterSection
                icon={Tag}
                title="Imprimante Étiquettes"
                subtitle="ZPL — Bixolon (50×30 mm)"
                widthHint="Largeur : Auto (50×30 mm)"
                ipValue={form.printer_ip}
                portValue={9100}
                onIpChange={(v) => upd('printer_ip', v)}
                onPortChange={() => { /* fixed 9100 for ZPL */ }}
                ipTestId={`magasin-printer-ip-${magasin.code}`}
                portTestId={`magasin-printer-port-${magasin.code}`}
                testTestId={`magasin-test-label-${magasin.code}`}
                onTest={() => runTest('label')}
                testing={labelTesting}
                testResult={labelTest}
                configured={!!form.printer_ip}
              />

              <PrinterSection
                icon={Receipt}
                title="Imprimante Tickets caisse + réparation"
                subtitle="ESC/POS 80mm — Epson"
                widthHint="Largeur : 80 mm (fixe — font A, 42 cols)"
                ipValue={form.ticket_printer_ip}
                portValue={form.ticket_printer_port}
                onIpChange={(v) => upd('ticket_printer_ip', v)}
                onPortChange={(v) => upd('ticket_printer_port', v)}
                ipTestId={`magasin-ticket-printer-ip-${magasin.code}`}
                portTestId={`magasin-ticket-printer-port-${magasin.code}`}
                testTestId={`magasin-test-ticket-${magasin.code}`}
                onTest={() => runTest('ticket')}
                testing={ticketTesting}
                testResult={ticketTest}
                configured={!!form.ticket_printer_ip}
              />
            </div>

            <p className="text-[10px] text-gray-500 leading-relaxed">
              Le bouton « Test impression » envoie un job à l'agent local, qui
              le route vers l'IP du magasin (et non vers l'imprimante de la
              session POS active). Statut « Configurée » = IP renseignée ; la
              réussite réelle est confirmée après le test.
            </p>
          </section>

          {error && (
            <div className="bg-red-500/15 border border-red-500/30 text-red-200 rounded-lg p-2 text-xs" data-testid="magasin-edit-error">
              {error}
            </div>
          )}
        </div>

        <footer className="px-5 py-4 border-t border-white/10 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 text-sm"
            data-testid={`magasin-edit-cancel-${magasin.code}`}
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={busy}
            className="px-4 py-2 rounded-lg bg-emerald-500/20 border border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/30 disabled:opacity-40 text-sm font-medium flex items-center gap-2"
            data-testid={`magasin-edit-save-${magasin.code}`}
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Enregistrer
          </button>
        </footer>
      </div>
    </div>,
    document.body,
  );
};

export default EditMagasinModal;
